import strapiClient from '../strapi/config';

export const strapiTrainingService = {
    /**
     * Récupérer toutes les formations publiées (endpoint public)
     */
    getPublishedTrainings: async () => {
        try {
            const response = await strapiClient.get('/api/trainings/published');
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error?.message || 'Erreur lors de la récupération des formations');
        }
    },

    /**
     * Récupérer une formation par slug (endpoint public)
     */
    getTrainingBySlug: async (slug) => {
        try {
            if (!slug) {
                throw new Error('Slug requis');
            }

            const response = await strapiClient.get(`/api/trainings/slug/${slug}`);
            return response.data;
        } catch (error) {
            if (error.response?.status === 404) {
                throw new Error('Formation non trouvée');
            }
            throw new Error(error.response?.data?.error?.message || 'Erreur lors de la récupération de la formation');
        }
    },

    /**
     * Récupérer les formations de l'utilisateur connecté
     */
    getUserTrainings: async () => {
        try {
            const token = localStorage.getItem('strapiToken');
            
            if (!token) {
                throw new Error('Utilisateur non connecté');
            }

            const response = await strapiClient.get('/api/trainings/my-trainings', {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            return response.data;
        } catch (error) {
            if (error.response?.status === 401) {
                throw new Error('Utilisateur non connecté');
            }
            throw new Error(error.response?.data?.error?.message || 'Erreur lors de la récupération de vos formations');
        }
    },

    /**
     * Accéder au contenu d'une formation (utilisateur connecté et ayant accès)
     */
    accessTraining: async (trainingId) => {
        try {
            const token = localStorage.getItem('strapiToken');
            
            if (!token) {
                throw new Error('Utilisateur non connecté');
            }

            if (!trainingId) {
                throw new Error('ID de formation requis');
            }

            const response = await strapiClient.get(`/api/trainings/${trainingId}/access`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            return response.data;
        } catch (error) {
            if (error.response?.status === 401) {
                throw new Error('Utilisateur non connecté');
            }
            if (error.response?.status === 403) {
                throw new Error('Accès non autorisé à cette formation');
            }
            if (error.response?.status === 404) {
                throw new Error('Formation non trouvée');
            }
            throw new Error(error.response?.data?.error?.message || 'Erreur lors de l\'accès à la formation');
        }
    },

    /**
     * Vérifier si l'utilisateur a accès à une formation spécifique
     */
    checkTrainingAccess: async (trainingId) => {
        try {
            await strapiTrainingService.accessTraining(trainingId);
            return true;
        } catch (error) {
            if (error.message.includes('Accès non autorisé')) {
                return false;
            }
            // Re-lancer l'erreur si ce n'est pas un problème d'accès
            throw error;
        }
    },

    /**
     * Récupérer la progression d'une formation pour l'utilisateur connecté
     */
    getTrainingProgress: async (trainingId) => {
        console.log('=== strapiTrainingService.getTrainingProgress ===');
        console.log('trainingId:', trainingId);
        
        try {
            const token = localStorage.getItem('strapiToken');
            console.log('Token disponible:', !!token);
            
            if (!token) {
                throw new Error('Utilisateur non connecté');
            }

            if (!trainingId) {
                throw new Error('ID de formation requis');
            }

            const url = `/api/trainings/${trainingId}/progress`;
            console.log('URL de la requête:', url);
            
            const response = await strapiClient.get(url, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            console.log('Réponse complète API:', response);
            console.log('Données de progression reçues:', response.data);
            
            return response.data;
        } catch (error) {
            console.error('Erreur dans getTrainingProgress:', error);
            console.error('Status:', error.response?.status);
            console.error('Data:', error.response?.data);
            
            if (error.response?.status === 401) {
                throw new Error('Utilisateur non connecté');
            }
            if (error.response?.status === 404) {
                throw new Error('Formation non trouvée');
            }
            throw new Error(error.response?.data?.error?.message || 'Erreur lors de la récupération de la progression');
        }
    },

    /**
     * Marquer une leçon comme complétée/non complétée
     */
    toggleLessonProgress: async (trainingId, chapterOrder, lessonOrder) => {
        console.log('=== strapiTrainingService.toggleLessonProgress ===');
        console.log('Paramètres:', { trainingId, chapterOrder, lessonOrder });
        console.log('Type trainingId:', typeof trainingId, 'Longueur:', trainingId?.length);

        try {
            const token = localStorage.getItem('strapiToken');
            console.log('Token disponible:', !!token);

            if (!token) {
                throw new Error('Utilisateur non connecté');
            }

            if (!trainingId || !chapterOrder || !lessonOrder) {
                throw new Error('Paramètres requis: trainingId, chapterOrder, lessonOrder');
            }

            const url = `/api/trainings/${trainingId}/chapters/${chapterOrder}/lessons/${lessonOrder}/progress`;
            console.log('URL de la requête:', url);

            const response = await strapiClient.post(
                url,
                {},
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            console.log('Réponse complète API:', response);
            console.log('Données de toggle reçues:', response.data);

            return response.data;
        } catch (error) {
            console.error('Erreur dans toggleLessonProgress:', error);
            console.error('Status:', error.response?.status);
            console.error('Data:', error.response?.data);

            if (error.response?.status === 401) {
                throw new Error('Utilisateur non connecté');
            }
            if (error.response?.status === 403) {
                throw new Error('Accès non autorisé');
            }
            if (error.response?.status === 404) {
                throw new Error('Formation, chapitre ou leçon non trouvé');
            }
            throw new Error(error.response?.data?.error?.message || 'Erreur lors de la mise à jour de la progression');
        }
    },

    /**
     * Récupérer la progression globale de l'utilisateur
     */
    getGlobalProgress: async () => {
        try {
            const token = localStorage.getItem('strapiToken');
            
            if (!token) {
                throw new Error('Utilisateur non connecté');
            }

            const response = await strapiClient.get('/api/progress/my-progress', {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            return response.data;
        } catch (error) {
            if (error.response?.status === 401) {
                throw new Error('Utilisateur non connecté');
            }
            throw new Error(error.response?.data?.error?.message || 'Erreur lors de la récupération de la progression globale');
        }
    }
};